<template>
  <auth-form
    :val-error="valError"
    :error-message="errorMessage"
    :show-logging-in="showLoggingIn"
    :logging-in="loggingIn || showLoggingIn || !isFormValid"
    action-text=""
    link-text=""
    link-label=""
    link-to=""
    :show-social-logins="false"
    :show-button="!successMessage"
    @submit="handleSubmit"
  >
    <template #title>
      <h2>Set a new password</h2>
      <p v-if="!successMessage">Create a secure password for your account.</p>
    </template>
    <template #form-fields>
      <template v-if="successMessage">
        <div class="success-container">
          <p class="success-text">Password changed successfully, we will redirect you to login.</p>
        </div>
      </template>
      <template v-else>
        <label for="password">New password *</label>
        <div class="password-field">
          <input
            :type="showPassword ? 'text' : 'password'"
            id="password"
            :value="password"
            placeholder="Enter your new password"
            @input="onPasswordInput"
          />
          <button
            type="button"
            class="toggle-visibility"
            :aria-label="showPassword ? 'Hide password' : 'Show password'"
            :aria-pressed="showPassword ? 'true' : 'false'"
            @click="showPassword = !showPassword"
          >
            <svg v-if="showPassword" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
              <line x1="1" y1="1" x2="23" y2="23"/>
            </svg>
            <svg v-else viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
        </div>

        <p class="requirements-title">Password requirements:</p>
        <ul class="requirements-list">
          <li v-for="req in requirements" :key="req.label" :class="req.state">
            <span class="req-icon" aria-hidden="true">{{ icon(req.state) }}</span>
            <span class="sr-only">{{ stateLabel(req.state) }}:</span>
            {{ req.label }}
          </li>
        </ul>

        <label for="passwordRepeat">Confirm new password *</label>
        <div class="password-field">
          <input
            :type="showConfirm ? 'text' : 'password'"
            id="passwordRepeat"
            :value="password_confirmation"
            placeholder="Enter your new password again"
            @input="onConfirmInput"
            @blur="confirmTouched = true"
          />
          <button
            type="button"
            class="toggle-visibility"
            :aria-label="showConfirm ? 'Hide password' : 'Show password'"
            :aria-pressed="showConfirm ? 'true' : 'false'"
            @click="showConfirm = !showConfirm"
          >
            <svg v-if="showConfirm" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
              <line x1="1" y1="1" x2="23" y2="23"/>
            </svg>
            <svg v-else viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
        </div>
        <div class="match-indicator" :class="matchState">
          <span class="req-icon" aria-hidden="true">{{ icon(matchState) }}</span>
          <span class="sr-only">{{ stateLabel(matchState) }}:</span>
          {{ matchText }}
        </div>
      </template>
    </template>
    <template v-if="!successMessage" #button-text>
      Save new password
    </template>
  </auth-form>
</template>

<script>
import AuthForm from "./AuthForm.vue";
import { mapState } from "vuex";
import { apiService } from "../../_services";
import router from "../../router";

export default {
  components: { AuthForm },
  data() {
    return {
      password: "",
      password_confirmation: "",
      submitted: false,
      valError: false,
      errorMessage: "",
      showLoggingIn: false,
      successMessage: false,
      showPassword: false,
      showConfirm: false,
      passwordTouched: false,
      confirmTouched: false,
      passwordLengthMet: false,
      uppercaseMet: false,
      lowercaseMet: false,
      numberMet: false,
      specialCharMet: false
    };
  },
  computed: {
    ...mapState("authentication", ["loggingIn"]),
    requirements() {
      return [
        { label: "At least 8 characters", met: this.passwordLengthMet },
        { label: "One uppercase letter", met: this.uppercaseMet },
        { label: "One lowercase letter", met: this.lowercaseMet },
        { label: "One number", met: this.numberMet },
        { label: "One special character", met: this.specialCharMet }
      ].map((r) => ({ ...r, state: this.requirementState(r.met) }));
    },
    matchState() {
      if (
        this.password &&
        this.password_confirmation &&
        this.password === this.password_confirmation
      ) {
        return "met";
      }
      if (
        (this.confirmTouched || this.submitted) &&
        this.password_confirmation &&
        this.password !== this.password_confirmation
      ) {
        return "error";
      }
      if (this.submitted && !this.password_confirmation) {
        return "error";
      }
      return "neutral";
    },
    matchText() {
      const texts = {
        met: "Passwords match",
        error: "Passwords do not match",
        neutral: "Both passwords must match"
      };
      return texts[this.matchState];
    },
    isFormValid() {
      return (
        this.passwordLengthMet &&
        this.uppercaseMet &&
        this.lowercaseMet &&
        this.numberMet &&
        this.specialCharMet &&
        !!this.password_confirmation &&
        this.password === this.password_confirmation
      );
    }
  },
  created() {
    document.title = "Quanspire | Set Password";
  },
  methods: {
    icon(state) {
      return { met: "✓", error: "✕", neutral: "○" }[state];
    },
    stateLabel(state) {
      return { met: "Met", error: "Not met", neutral: "Pending" }[state];
    },
    requirementState(met) {
      if (met) return "met";
      if (this.submitted || (this.passwordTouched && this.password.length > 0)) {
        return "error";
      }
      return "neutral";
    },
    validatePassword() {
      this.passwordLengthMet = this.password.length >= 8;
      this.uppercaseMet = /[A-Z]/.test(this.password);
      this.lowercaseMet = /[a-z]/.test(this.password);
      this.numberMet = /[0-9]/.test(this.password);
      this.specialCharMet = /[^\p{L}\p{N}]/u.test(this.password);
    },
    onPasswordInput(event) {
      this.password = event.target.value;
      this.passwordTouched = true;
      this.validatePassword();
    },
    onConfirmInput(event) {
      this.password_confirmation = event.target.value;
      this.confirmTouched = true;
    },
    async handleSubmit() {
      this.submitted = true;

      if (!this.password) {
        this.valError = true;
        this.errorMessage = "New password is required";
        this.showLoggingIn = false;
        return;
      }

      if (!this.password_confirmation) {
        this.valError = true;
        this.errorMessage = "Password confirmation is required";
        this.showLoggingIn = false;
        return;
      }

      if (this.password !== this.password_confirmation) {
        this.valError = true;
        this.errorMessage = "Passwords do not match";
        this.showLoggingIn = false;
        return;
      }

      if (
        !(
          this.passwordLengthMet &&
          this.uppercaseMet &&
          this.lowercaseMet &&
          this.numberMet &&
          this.specialCharMet
        )
      ) {
        this.valError = true;
        this.errorMessage = "Password does not meet all requirements";
        this.showLoggingIn = false;
        return;
      }

      this.valError = false;
      this.errorMessage = "";
      this.showLoggingIn = true;
      try {
        const user = {
          pk_idusers: this.$route.params.id,
          password: this.password,
          password_confirmation: this.password_confirmation
        };
        await apiService.setRessetPassword(user);
        this.showLoggingIn = false;
        this.successMessage = true;
        setTimeout(() => {
          router.push("/login");
        }, 5000);
      } catch (error) {
        this.valError = true;
        this.errorMessage =
          (error.response && error.response.data && error.response.data.password) ||
          error.message ||
          "An error occurred. Please try again.";
        this.showLoggingIn = false;
      }
    }
  }
};
</script>

<style scoped>
.password-field {
  position: relative;
  margin-top: 5px;
}

.password-field input {
  margin-top: 0;
  padding-right: 42px;
  width: 100%;
}

.toggle-visibility {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  margin: 0;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 5px;
  color: var(--textInactiveFixed);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.2s ease;
}

.toggle-visibility:hover {
  color: var(--textactive-2);
  background: transparent;
}

.requirements-title {
  margin: 15px 0 6px;
  font-size: 13px;
  color: var(--textactive-2);
}

.requirements-list {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 13px;
}

.requirements-list li {
  display: flex;
  align-items: center;
  margin: 6px 0;
  color: var(--textactive-2);
  transition: color 0.2s ease;
}

.req-icon {
  display: inline-flex;
  justify-content: center;
  width: 18px;
  margin-right: 8px;
  font-weight: bold;
}

.requirements-list li.neutral .req-icon,
.match-indicator.neutral .req-icon {
  color: var(--textInactiveFixed);
}

.requirements-list li.met,
.match-indicator.met {
  color: var(--green, #28a745);
}

.requirements-list li.met .req-icon,
.match-indicator.met .req-icon {
  color: var(--green, #28a745);
}

.requirements-list li.error,
.match-indicator.error {
  color: var(--red);
}

.requirements-list li.error .req-icon,
.match-indicator.error .req-icon {
  color: var(--red);
}

.match-indicator {
  display: flex;
  align-items: center;
  margin-top: 8px;
  font-size: 13px;
  color: var(--textactive-2);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.success-container {
  text-align: center;
  padding: 20px;
}

.success-text {
  color: var(--green, #28a745) !important;
  font-size: 16px;
  margin: 0;
}
</style>
